import type { Course, CourseResult, CourseStep } from './course.js'
import { finalize, initialScoring, recordAnswer, type ScoringState } from './scoring.js'

/**
 * Stavový automat průchodu kurzem. Čistá funkce — žádný React, žádný DOM,
 * žádné čtení hodin. Čas se do akcí dosazuje zvenčí (`now`), takže je
 * celý průchod deterministicky testovatelný.
 *
 * Nahrazuje implicitní stav původní aplikace (`objectIndex` + `index` 0/1/2 +
 * `questionAnswered` + `visible`), měněný z osmi různých míst.
 */

export type PlayerError =
  | { kind: 'courseLoad'; message: string }
  | { kind: 'video'; message: string }

export type PlayerPhase =
  | { kind: 'loading' }
  | { kind: 'error'; error: PlayerError }
  /** Video před otázkou; u kroku typu `video` je to obsah samotný. */
  | { kind: 'introVideo' }
  | { kind: 'question' }
  /** Otázka zodpovězena, čeká se na kliknutí pro pokračování. */
  | { kind: 'feedback'; correct: boolean }
  | { kind: 'outroVideo'; correct: boolean }
  | { kind: 'finished'; result: CourseResult }

export interface LastAnswer {
  selected: number
  correct: boolean
  elapsedSec: number
}

export interface PlayerState {
  phase: PlayerPhase
  course: Course | null
  /**
   * Fronta pozic do `course.steps`. Špatně zodpovězená otázka se do fronty
   * vloží znovu — proto se tu opakují hodnoty.
   */
  queue: readonly number[]
  /** Ukazatel do `queue`, ne do `course.steps`. */
  cursor: number
  /**
   * Počet pokusů podle POZICE kroku.
   *
   * Původní aplikace počítala pokusy podle pole `index`, které je v datech
   * duplicitní — odpověď na jednu otázku tak zvyšovala počet pokusů i u jiné,
   * nesouvisející otázky se stejným indexem, a uživatel dostal méně bodů.
   * Týká se 8 z 12 modulů. Viz skill `safetutor-contract` §6 — záměrná oprava.
   */
  attempts: Readonly<Record<number, number>>
  scoring: ScoringState
  /** Timestamp v ms, kdy se zobrazila aktuální otázka. Mimo otázku null. */
  questionStartedAt: number | null
  /** Timestamp začátku pauzy, nebo null když kurz běží. */
  pausedAt: number | null
  /** Kolik ms už uživatel u aktuální otázky strávil v pauze. */
  pausedTotalMs: number
  lastAnswer: LastAnswer | null
}

/** Uložený postup, který lze vrátit do automatu po refreshi. */
export interface RestorePoint {
  queue: readonly number[]
  cursor: number
  attempts: Readonly<Record<number, number>>
  totalPoints: number
  totalMaxPoints: number
  answers: ScoringState['answers']
}

export type PlayerAction =
  | { type: 'courseLoaded'; course: Course; now: number; restore?: RestorePoint | undefined }
  | { type: 'courseFailed'; message: string }
  | { type: 'videoEnded'; now: number }
  | { type: 'videoFailed'; message: string }
  /** Uživatel přeskočí video, které se nepodařilo přehrát. */
  | { type: 'skipVideo'; now: number }
  | { type: 'answer'; selected: number; now: number }
  /** Kliknutí kdekoliv po zodpovězení otázky. */
  | { type: 'continue'; now: number }
  | { type: 'pause'; now: number }
  | { type: 'resume'; now: number }

export function initialPlayerState(): PlayerState {
  return {
    phase: { kind: 'loading' },
    course: null,
    queue: [],
    cursor: 0,
    attempts: {},
    scoring: initialScoring(),
    questionStartedAt: null,
    pausedAt: null,
    pausedTotalMs: 0,
    lastAnswer: null,
  }
}

export function isPaused(state: PlayerState): boolean {
  return state.pausedAt !== null
}

/* ------------------------------------------------------------------ *
 * Selektory
 * ------------------------------------------------------------------ */

export function currentStep(state: PlayerState): CourseStep | null {
  if (!state.course) return null
  const position = state.queue[state.cursor]
  if (position === undefined) return null
  return state.course.steps[position] ?? null
}

/**
 * Sekundy strávené na aktuální otázce — odvozeno z timestampu.
 *
 * Původní aplikace držela čítač v `setInterval` a inkrementovala ho přes
 * zastaralý `this.state`, takže hodnota přetékala mezi otázkami a časová
 * penalizace padala skoro na každou otázku neprávem.
 */
export function elapsedSec(state: PlayerState, now: number): number {
  if (state.questionStartedAt === null) return 0

  // Během pauzy čas stojí — počítá se ke stavu na jejím začátku.
  const end = state.pausedAt ?? now
  return Math.max(0, Math.floor((end - state.questionStartedAt - state.pausedTotalMs) / 1000))
}

export type TimerLevel = 'none' | 'warning' | 'over'

export function timerLevel(state: PlayerState, now: number): TimerLevel {
  const step = currentStep(state)
  if (!step || state.questionStartedAt === null) return 'none'

  const seconds = elapsedSec(state, now)
  if (seconds > step.timeLimitSec) return 'over'
  if (seconds > step.timeWarningSec) return 'warning'
  return 'none'
}

export function isAnswerCorrect(step: CourseStep, selected: number): boolean {
  return step.kind === 'highlight'
    ? selected === step.correctHotspot
    : selected === step.correctAnswer
}

/** Kolikátý pokus by byl ten následující, 1-based. */
export function nextAttemptNumber(state: PlayerState): number {
  const position = state.queue[state.cursor]
  if (position === undefined) return 1
  return (state.attempts[position] ?? 0) + 1
}

/* ------------------------------------------------------------------ *
 * Přechody
 * ------------------------------------------------------------------ */

function finish(state: PlayerState): PlayerState {
  const pointsToPass = state.course?.pointsToPassPercent ?? 0
  return {
    ...state,
    phase: { kind: 'finished', result: finalize(state.scoring, pointsToPass) },
    questionStartedAt: null,
  }
}

/**
 * Vstup do kroku na dané pozici ve frontě.
 *
 * Prázdné `videoUrl` se řeší TADY, v reduceru — původní aplikace kvůli tomu
 * volala `setState` přímo z `render()`.
 */
function enterStep(state: PlayerState, cursor: number, now: number): PlayerState {
  if (!state.course) return state
  if (cursor >= state.queue.length) return finish({ ...state, cursor })

  const position = state.queue[cursor]
  const step = position === undefined ? undefined : state.course.steps[position]
  if (!step) return finish({ ...state, cursor })

  const base: PlayerState = {
    ...state,
    cursor,
    questionStartedAt: null,
    // Pauza se váže k jedné otázce — nový krok začíná s čistým počítadlem.
    pausedAt: null,
    pausedTotalMs: 0,
    lastAnswer: null,
  }

  if (step.videoUrl !== '') {
    return { ...base, phase: { kind: 'introVideo' } }
  }

  // Krok typu `video` bez URL nemá co zobrazit — rovnou dál.
  if (step.kind === 'video') {
    return enterStep(base, cursor + 1, now)
  }

  return { ...base, phase: { kind: 'question' }, questionStartedAt: now }
}

/** Po intro videu: u kroku `video` jdeme dál, jinak se ukáže otázka. */
function afterIntroVideo(state: PlayerState, now: number): PlayerState {
  const step = currentStep(state)
  if (!step) return finish(state)

  if (step.kind === 'video') return enterStep(state, state.cursor + 1, now)

  return {
    ...state,
    phase: { kind: 'question' },
    questionStartedAt: now,
    pausedAt: null,
    pausedTotalMs: 0,
  }
}

/**
 * Zařadí špatně zodpovězenou otázku znovu do fronty.
 * Replikuje `shouldRepeatQuestion` — vloží kopii mezi aktuální pozici a konec.
 */
function requeue(queue: readonly number[], cursor: number, position: number, rand: number): number[] {
  const min = cursor + 1
  const max = queue.length
  const at = min + Math.floor(rand * (max - min + 1))
  const next = [...queue]
  next.splice(Math.min(at, max), 0, position)
  return next
}

export interface ReducerOptions {
  /** Injektovatelný generátor pro deterministické testy. */
  random?: () => number
}

export function createPlayerReducer(options: ReducerOptions = {}) {
  const random = options.random ?? Math.random

  return function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
    switch (action.type) {
      case 'courseLoaded': {
        const { course, restore } = action

        // Uložený postup se přijme jen tehdy, když sedí na načtený kurz.
        // Kdyby se obsah modulu mezitím změnil, začne se raději od začátku.
        const valid =
          restore !== undefined &&
          restore.queue.length > 0 &&
          restore.cursor >= 0 &&
          restore.cursor <= restore.queue.length &&
          restore.queue.every((p) => p >= 0 && p < course.steps.length)

        const loaded: PlayerState = {
          ...state,
          course,
          queue: valid ? [...restore.queue] : course.steps.map((_, i) => i),
          cursor: valid ? restore.cursor : 0,
          attempts: valid ? { ...restore.attempts } : {},
          scoring: valid
            ? {
                totalPoints: restore.totalPoints,
                totalMaxPoints: restore.totalMaxPoints,
                answers: [...restore.answers],
              }
            : initialScoring(),
          lastAnswer: null,
        }

        if (course.steps.length === 0) return finish(loaded)
        return enterStep(loaded, loaded.cursor, action.now)
      }

      case 'courseFailed':
        return {
          ...state,
          phase: { kind: 'error', error: { kind: 'courseLoad', message: action.message } },
        }

      case 'videoFailed':
        // Nikdy nezamrznout — chybový stav nabídne přeskočení.
        return {
          ...state,
          phase: { kind: 'error', error: { kind: 'video', message: action.message } },
        }

      case 'skipVideo': {
        // Přeskočení se chová jako doběhnutí videa, ať už šlo o intro nebo outro.
        if (state.phase.kind === 'outroVideo') return enterStep(state, state.cursor + 1, action.now)
        if (state.lastAnswer !== null) return enterStep(state, state.cursor + 1, action.now)
        return afterIntroVideo(state, action.now)
      }

      case 'videoEnded': {
        if (state.phase.kind === 'introVideo') return afterIntroVideo(state, action.now)
        if (state.phase.kind === 'outroVideo') return enterStep(state, state.cursor + 1, action.now)
        return state
      }

      case 'answer': {
        if (state.phase.kind !== 'question') return state

        const step = currentStep(state)
        const position = state.queue[state.cursor]
        if (!step || position === undefined) return state

        const correct = isAnswerCorrect(step, action.selected)
        const attempt = (state.attempts[position] ?? 0) + 1
        const seconds = elapsedSec(state, action.now)

        const scoring = recordAnswer(state.scoring, {
          step,
          attempt,
          wasCorrect: correct,
          selectedAnswer: action.selected,
          elapsedSec: seconds,
        })

        const queue = correct
          ? state.queue
          : requeue(state.queue, state.cursor, position, random())

        return {
          ...state,
          phase: { kind: 'feedback', correct },
          queue,
          attempts: { ...state.attempts, [position]: attempt },
          scoring,
          questionStartedAt: null,
          lastAnswer: { selected: action.selected, correct, elapsedSec: seconds },
        }
      }

      case 'pause': {
        if (state.pausedAt !== null) return state
        return { ...state, pausedAt: action.now }
      }

      case 'resume': {
        if (state.pausedAt === null) return state
        return {
          ...state,
          pausedAt: null,
          pausedTotalMs: state.pausedTotalMs + Math.max(0, action.now - state.pausedAt),
        }
      }

      case 'continue': {
        if (state.phase.kind !== 'feedback') return state

        const step = currentStep(state)
        if (!step) return finish(state)

        const correct = state.phase.correct
        const url = correct ? step.correctVideoUrl : step.wrongVideoUrl

        if (url === '') return enterStep(state, state.cursor + 1, action.now)

        return { ...state, phase: { kind: 'outroVideo', correct } }
      }

      default: {
        const never: never = action
        return never
      }
    }
  }
}

/** Výchozí reducer pro aplikaci. Testy si vytvářejí vlastní s pevným `random`. */
export const playerReducer = createPlayerReducer()

/**
 * URL videa, které se má právě přehrát, nebo null.
 * UI se tak nemusí rozhodovat podle fáze.
 */
export function currentVideoUrl(state: PlayerState): string | null {
  const step = currentStep(state)
  if (!step) return null

  if (state.phase.kind === 'introVideo') return step.videoUrl || null
  if (state.phase.kind === 'outroVideo') {
    const url = state.phase.correct ? step.correctVideoUrl : step.wrongVideoUrl
    return url || null
  }
  return null
}
